import { getSafe } from "@jslib/shared/utils/getSafe";

import Message from "@components/Message";
import { PoweredByComponent } from "@components/PoweredBy/PoweredByComponent";
import { LogoComponent } from "@components/LogoComponent/LogoComponent";
import { LinkExternal } from "@components/LinkExternal";
import { TryAgainActionComponent } from "@components/TryAgainAction";
import { AnnouncingHeader } from "@components/AnnouncingHeader/AnnouncingHeader";

import {
  type VerificationService,
  type ErrorId,
  type ErrorResponse,
  type ErrorDetailId,
} from "@/lib/types/types";
import { VerificationStepsEnum } from "@/lib/types/runtimeTypes";
import { getOption } from "@/options/options";
import { recordEvent } from "@/lib/GoogleAnalytics/ga";
import { type MessageId } from "@/lib/intl/types";
import { isValidHttpUrl } from "@/lib/utils/routing/Url";

import { useFlag } from "../FeatureFlags/hooks";
interface StepErrorProps {
  verificationService: VerificationService;
  errorId?: ErrorId;
}

// Error IDs for which we surface the "contact support" message above the action
// button and hide the "Get help" link in the footer. Add or remove IDs here.
const CONTACT_SUPPORT_ERROR_IDS: ErrorId[] = [
  "docReviewLimitExceeded",
  "verificationLimitExceeded",
  "fraudRulesReject",
];

const ErrorTitle = ({
  verificationService,
  errorIds,
}: {
  verificationService: VerificationService;
  errorIds: ErrorId[];
}) => {
  const firstErrorId = errorIds[0];
  const errorMessageId: MessageId = verificationService?.messages[
    `step.error.errorId.${firstErrorId}.title`
  ]
    ? `step.error.errorId.${firstErrorId}.title`
    : "step.error.title";

  return (
    <AnnouncingHeader align="center">
      <h1>
        <Message id={errorMessageId} />
      </h1>
    </AnnouncingHeader>
  );
};

const ErrorMsg = ({
  errorIds,
  errorDetailId,
}: {
  errorIds: ErrorId[];
  errorDetailId: ErrorDetailId;
}) => {
  const displayErrorMessageIds = new Map();
  if (errorDetailId) {
    displayErrorMessageIds.set(errorDetailId, `errorDetailId.${errorDetailId}`);
  } else {
    errorIds?.forEach((errId) => displayErrorMessageIds.set(errId, `errorId.${errId}`));
  }

  if (displayErrorMessageIds.size > 0) {
    return (
      <p>
        {Array.from(displayErrorMessageIds).map(([errId, errMsgId]) => (
          <Message key={errMsgId} id={errMsgId} />
        ))}
      </p>
    );
  }

  return null;
};

/**
 * Supplementary guidance for an error, shown muted beneath the main error message.
 * Rendered only for errorIds that define a `step.error.errorId.<errorId>.note` message.
 */
const ErrorNote = ({
  verificationService,
  errorIds,
}: {
  verificationService: VerificationService;
  errorIds: ErrorId[];
}) => {
  const noteMessageId: MessageId = `step.error.errorId.${errorIds[0]}.note`;
  if (!verificationService.messages?.[noteMessageId]) return null;

  return (
    <p className="sid-error-note sid-h-small-text">
      <Message id={noteMessageId} />
    </p>
  );
};

const ErrorButton = ({
  verificationService,
  errorIds,
  redirectUrl,
}: {
  verificationService: VerificationService;
  errorIds: ErrorId[];
  redirectUrl?: string;
}) => {
  if (errorIds.includes("unauthorizedAccountStatus")) {
    const faqUrl = getSafe(() => verificationService.programTheme.config.brandInfo.faqUrl);
    if (!faqUrl) return null;
    return (
      <LinkExternal
        href={faqUrl}
        onClick={() => recordEvent(VerificationStepsEnum.error, faqUrl)}
        className="sid-btn sid-faq-btn sid-btn-light"
      >
        <Message id="step.error.errorId.unauthorizedAccountStatus.buttonText" />
      </LinkExternal>
    );
  }

  if (errorIds.includes("verificationLimitExceeded")) return null;

  if (
    errorIds.includes("missingRequiredMetadata") ||
    errorIds.includes("missingRequiredExternalId")
  ) {
    if (getOption("tryAgainAction").type === "reset") return null;
    return <TryAgainActionComponent />;
  }

  const firstErrorId = errorIds[0];
  // A redirectUrl with any scheme other than http(s) is treated as absent so it is never linked to
  if (
    verificationService.messages?.[`step.error.errorId.${firstErrorId}.buttonText`] &&
    isValidHttpUrl(redirectUrl)
  ) {
    return (
      <LinkExternal
        href={redirectUrl}
        onClick={() => recordEvent(VerificationStepsEnum.error, redirectUrl)}
        className="sid-btn sid-error-btn sid-btn-light"
      >
        <Message id={`step.error.errorId.${firstErrorId}.buttonText`} />
      </LinkExternal>
    );
  }

  return <TryAgainActionComponent />;
};

export const StepErrorComponent = ({ verificationService, errorId }: StepErrorProps) => {
  const verificationResponse = verificationService.verificationResponse as ErrorResponse;
  const { errorIds: responseErrorIds, redirectUrl, errorDetailId } = verificationResponse;
  const shouldHideIcon = useFlag("hc-my-sheer-id-theme-builder");

  const errorIdsSet = new Set<ErrorId>();
  if (errorId) errorIdsSet.add(errorId);
  if (Array.isArray(responseErrorIds)) responseErrorIds.forEach((errId) => errorIdsSet.add(errId));
  const errorIds = Array.from(errorIdsSet);

  const showContactSupport = errorIds.some((errId) => CONTACT_SUPPORT_ERROR_IDS.includes(errId));

  return (
    <div id="sid-step-error" className="sid-error-container sid-l-container">
      <div className="sid-header sid-l-horz-center" role="banner">
        <div className="sid-error-logo-container sid-l-horz-center">
          <LogoComponent
            verificationService={verificationService}
            fallbackLogo={
              !shouldHideIcon && (
                <img
                  className="sid-error-logo sid-l-lead-image"
                  alt=""
                  src="https://assets-resources.sheerid.com/common/images/2018/icons/circle-exclamation.svg"
                />
              )
            }
          />
        </div>
        <ErrorTitle verificationService={verificationService} errorIds={errorIds} />
        <div className="sid-header__subtitle sid-l-horz-center sid-l-space-top-md">
          <div className="sid-error-msg sid-l-horz-center">
            <ErrorMsg errorIds={errorIds} errorDetailId={errorDetailId} />
            <ErrorNote verificationService={verificationService} errorIds={errorIds} />
          </div>
          {showContactSupport && (
            <p className="sid-error-contact-support sid-l-space-top-md">
              <Message id="contactSupport" />
            </p>
          )}
          <div className="sid-l-space-top-lg sid-error-button-container sid-l-horz-center">
            <ErrorButton
              verificationService={verificationService}
              errorIds={errorIds}
              redirectUrl={redirectUrl}
            />
          </div>
        </div>
      </div>

      <div className="sid-footer sid-powered-by-container sid-l-horz-center sid-l-space-top-xl sid-h-small-text">
        <PoweredByComponent
          verificationService={verificationService}
          isCentered
          hideFaqLink={showContactSupport}
        />
      </div>
    </div>
  );
};
